import React from "react";
import SEO from "../../components/g4/SEO";
import { G4Navbar, G4Footer } from "../../components/g4/Chrome";
import ObfuscatedEmail from "../../components/ObfuscatedEmail";

/**
 * Studio G4 Privacy Policy.
 *
 * Structurally mirrors the LRS /privacy page (same section ordering,
 * same information commitments) but with:
 *   • Studio G4 Ltd as the sole operator identity (LRS references
 *     stripped — Studio G4 is the parent entity, so the companies
 *     house details are retained verbatim).
 *   • Form data model updated to the /request-quote fields (not the
 *     LRS free-design enquiry fields).
 *   • Contact email → info@studio-g4.co.uk.
 *   • Free-design-service references removed; replaced with generic
 *     "architecture, project management and construction services" language.
 *   • Geography generalised.
 */

function LegalHero({ eyebrow, title, intro }) {
    return (
        <section className="bg-[#0A0A0A] text-white pt-32 pb-14 md:pt-40 md:pb-20">
            <div className="max-w-[720px] mx-auto px-6 md:px-10">
                <p className="font-['Manrope'] font-semibold text-[12px] tracking-[0.22em] uppercase mb-4" style={{ color: "#650505" }}>
                    {eyebrow}
                </p>
                <h1 className="font-['Outfit'] text-[38px] md:text-[52px] font-light leading-[1.05] tracking-tight mb-4">
                    {title}
                </h1>
                {intro && (
                    <p className="font-['Manrope'] text-white/70 text-[16px] md:text-[18px] leading-relaxed">
                        {intro}
                    </p>
                )}
            </div>
        </section>
    );
}

function LegalBody({ children }) {
    return (
        <section className="bg-white py-16 md:py-24">
            <div className="max-w-[720px] mx-auto px-6 md:px-10 font-['Manrope'] text-[17px] leading-[1.75] text-[#0A0A0A]/85 space-y-5">
                {children}
            </div>
        </section>
    );
}

function H2({ children }) {
    return (
        <h2 className="font-['Outfit'] text-[#0A0A0A] text-[24px] md:text-[28px] mt-12 mb-4 leading-tight font-normal">
            {children}
        </h2>
    );
}

function LastUpdated({ date = "17 October 2026" }) {
    return (
        <p className="font-['Manrope'] text-[#0A0A0A]/50 text-[14px] italic">
            Last updated: {date}
        </p>
    );
}

export default function Privacy() {
    return (
        <div data-testid="g4-privacy-page">
            <SEO
                title="Privacy Policy"
                description="How Studio G4 handles information you share with us through our website and quote-request form."
                path="/privacy"
            />
            <G4Navbar />
            <LegalHero
                eyebrow="Privacy"
                title="Privacy Policy"
                intro="How we handle information you share with us through our website and quote-request form."
            />
            <LegalBody>
                <LastUpdated />

                <H2>Who we are</H2>
                <p>
                    This website is operated by Studio G4 Ltd, a company registered in England and Wales under
                    company number 12308705, with registered office at 10 Barley Mow Passage, Chiswick, London,
                    W4 4PH.
                </p>
                <p>
                    For any privacy-related questions, you can contact us at{" "}
                    <ObfuscatedEmail
                        user="info"
                        domain="studio-g4.co.uk"
                        className="underline hover:opacity-70"
                    />
                    .
                </p>

                <H2>What information we collect</H2>
                <p>
                    When you submit a quote request through this website, we collect the information you provide,
                    which typically includes:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>your name;</li>
                    <li>your email address;</li>
                    <li>your telephone number (if provided);</li>
                    <li>the location of the proposed project;</li>
                    <li>the type of project you are considering;</li>
                    <li>a short description of what you are hoping to achieve;</li>
                    <li>your anticipated construction budget;</li>
                    <li>your preferred project timeline;</li>
                    <li>the approximate site or floor area (if provided);</li>
                    <li>any reference or inspiration links you choose to share.</li>
                </ul>
                <p>
                    We may also automatically capture basic technical information about how you arrived at our
                    website so that we can understand which channels reach the people we work with. We do not
                    use behavioural advertising trackers or third-party analytics cookies.
                </p>

                <H2>Why we collect it</H2>
                <p>We collect this information only to:</p>
                <ul className="list-disc pl-6 space-y-2">
                    <li>assess your project enquiry;</li>
                    <li>consider whether we are a suitable fit for your brief;</li>
                    <li>contact you to discuss your project;</li>
                    <li>prepare proposals or quotations where appropriate;</li>
                    <li>manage the resulting client relationship if you appoint us.</li>
                </ul>
                <p>
                    We do not operate a general marketing programme against enquiry data. We do not sell your
                    information. We do not add you to a mailing list.
                </p>

                <H2>How long we keep it</H2>
                <p>
                    If we do not proceed with your enquiry, we will normally delete your enquiry data once the
                    enquiry is concluded, subject to any reasonable short period necessary for administration or
                    legal purposes.
                </p>
                <p>
                    If you go on to work with us, we retain the information required to deliver your project,
                    keep proper business records, and meet our legal and regulatory obligations.
                </p>

                <H2>Your rights</H2>
                <p>
                    Under UK data protection law you have the right to ask us for a copy of the information we
                    hold about you, to correct it if it is inaccurate, to ask us to delete it, and to object to
                    certain uses of it. You can exercise any of these rights by contacting us at{" "}
                    <ObfuscatedEmail
                        user="info"
                        domain="studio-g4.co.uk"
                        className="underline hover:opacity-70"
                    />
                    .
                </p>
                <p>
                    You also have the right to complain to the UK Information Commissioner's Office (
                    <a
                        className="underline hover:opacity-70"
                        href="https://ico.org.uk"
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        ico.org.uk
                    </a>
                    ) if you are unhappy with how we have handled your information.
                </p>

                <H2>Cookies</H2>
                <p>
                    This website uses only essential cookies required for the site to function correctly and be
                    administered securely. We do not use advertising, marketing or behavioural tracking cookies.
                    See our{" "}
                    <a className="underline hover:opacity-70" href="/cookies">
                        Cookie Notice
                    </a>{" "}
                    for details.
                </p>

                <H2>Changes to this policy</H2>
                <p>
                    We may update this policy from time to time. The "last updated" date at the top of this page
                    indicates when the current version was published.
                </p>
            </LegalBody>
            <G4Footer />
        </div>
    );
}
